import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  MenuItem,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  Chip,
  InputLabel,
  Select,
  FormControl,
  Tab,
  Snackbar,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  Grid,
} from "@mui/material";
import { Product, SegmentType } from "../../types/ProductsForm";
import { FileMetadata } from "../../types/FileMetadata";
import { apiRequest } from "../../utils/apiClient";
import { getPresignedUrl, uploadToS3 } from "../../utils/s3utils";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { DiamondField, QuoteData, SolitaireField } from "../../types/Interface";
import { categoryOptions, clarities, colors, diamondCertificationOptions, diamonShapes, metalColors, metalPurity, occasionOptions, settingTypes, sieveSizes, solitaireSizes, subCategoryOptions, tagsOptions } from "../../constants/Constants";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { useVendorContext } from "../../context/VendorContext";



const defaultProduct: Product = {
  productCode: "",
  productName: "",
  segment: SegmentType.DIAMOND_JEWELLERY,
  category: categoryOptions[0] || "",
  subCategory: [],
  image: [],
  price: 0,
  tags: [],
  occasion: occasionOptions[0] || "",
  vendor: "",
  certification: "IGI",
  gender: "unisex",
  productSummary: {
    width: "",
    grossWt: "",
  },
  priceBreakup: {
    metal: 0,
    discountedDiamondPrice: 0,
    actualDiamondPrice: 0,
    discountMakingCharges: 0,
    makingCharges: 0,
    gst: 0,
    gstPercent: 0,
    total: 0,
    discountTotal: 0,
  },
  discountPercent: "",
  metalDetails: {
    metalName: metalColors[0] || "yellow gold",
    purity: Number(metalPurity[0]) || 18,
    metalWt: undefined,
  },
  diamondDetails: {
    totalNumberOfDiamonds: 0,
    totalWt: 0.0,
    diamonds: {
      clarity: "VVS",
      color: "EF",
      numberOfDiamonds: 0,
      settingType: "Solitaire",
      shape: "Round",
      diamondWtApprox: 0.0,
      additionalCharges: 0.0,
    },
    diamondEntries: [],
    solitaireEntries: [],
    solitaire: {
        stClarity: "VVS",
        stColor: "EF",
        numberOfStDiamonds: 0,
        stSettingType: "Solitaire",
        stShape: "Round",
        stDiamondWt: 0.0,
        availableSizes: []
    },
  },
  productDescription: "",
  makingPercentage: undefined,
  note: "",
  isCatalog: true,
  isInventory: false
};


interface AddProductProps {
  productList?: Product[];
  updateproduct?: Product;
  isEditMode?: boolean;
  isAddMode?: boolean;
  quoteData?: QuoteData;
  openDialog?: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddProduct: React.FC<AddProductProps> = ({updateproduct, isEditMode, isAddMode, quoteData, openDialog, productList }) => {
  const [product, setProduct] = useState<Product>(() => {
    if (isAddMode && isEditMode && quoteData && quoteData.product) {
      return quoteData.product;
    }
    if (isEditMode && updateproduct) return updateproduct;
    return defaultProduct;
  });
  
  const [selectedFile, setSelectedFile] = useState<FileMetadata[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [toastOpen, setToastOpen] = useState(false);
const [toastMessage, setToastMessage] = useState('');
const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');

const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
  setToastMessage(message);
  setToastSeverity(severity);
  setToastOpen(true);
};

const [subCategoryError, setSubCategoryError] = useState(false);
const { vendor } = useVendorContext();
const [validation, setValidation] = useState<{ 
  [key: string]: { isValid: boolean; message: string } 
}>({
  productName: { isValid: true, message: "" },
  productCode: { isValid: true, message: "" },
});


const handleSubCategoryChange = (event: any, newValue: string[]) => {
  setSubCategoryError(newValue.length === 0); // Mark error if empty
  handleChange("subCategory", newValue);
};



  const [diamondRows, setDiamondRows] = useState(  isEditMode && updateproduct?.diamondDetails?.diamondEntries?.length
    ? updateproduct.diamondDetails.diamondEntries
    : [{ diamondSize: sieveSizes[0] || "", diamondCount: 0, totalwt: 0.0 }]);

  const [solitaireRows, setSolitaireRows] = useState(  isEditMode && updateproduct?.diamondDetails?.solitaireEntries?.length
    ? updateproduct.diamondDetails.solitaireEntries
    : [{ solitaireSize: solitaireSizes[0] || 0.0, solitaireCount: 0 }]);

    const handleDiamondChange = (
      index: number,
      field: keyof DiamondField,
      value: string
    ) => {
      const updated = [...diamondRows];
      const numericValue = Number(value);
      updated[index] = {
        ...updated[index],
        [field]: field === "diamondCount" || field === "totalwt"
          ? Math.max(0, Number.isNaN(numericValue) ? 0 : numericValue)
          : value,
      };
      setDiamondRows(updated);
    };
    
    


  
  const addDiamondRow = () => {
    const updated = [...diamondRows, { diamondSize: sieveSizes[0] || "", diamondCount: 0, totalwt: 0.0 }];
    setDiamondRows(updated);
  };
  
  const removeDiamondRow = (index: number) => {
    const updated = [...diamondRows];
    updated.splice(index, 1);
    setDiamondRows(updated);
  };


  
  const handleSolitaireChange = (index: number, field: keyof SolitaireField, value: string) => {
    const updated = [...solitaireRows];
    const numericValue = Number(value);
    updated[index][field] = Math.max(0, Number.isNaN(numericValue) ? 0 : numericValue);
    setSolitaireRows(updated);
  };
  
  const addSolitaireRow = () => {
    const updated = [...solitaireRows, { solitaireSize: solitaireSizes[0] || 0.0, solitaireCount: 0 }];
    setSolitaireRows(updated);
  };
  
  const removeSolitaireRow = (index: number) => {
    const updated = [...solitaireRows];
    updated.splice(index, 1);
    setSolitaireRows(updated);
  };
  

const handleChange = (path: string, value: any) => {
  const keys = path.split(".");
  setProduct((prev) => {
    const copy = { ...prev };
    let obj: any = copy;
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        obj[key] = value;
      } else {
        obj = obj[key];
      }
    });

    // After updating value, check uniqueness
    if (path === "productName" || path === "productCode") {
      const exists = productList && productList.some(
        (p) =>
          p[path] === value &&
          p.productId !== copy.productId // Avoid matching self during edit
      );
      setValidation((prevVal) => ({
        ...prevVal,
        [path]: exists
          ? { isValid: false, message: `${path === "productName" ? "Product name" : "Product code"} already exists.` }
          : { isValid: true, message: "" },
      }));
    }

    return { ...copy };
  });
};


  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
  
      const newSelectedFiles = files.map((file, index) => {
        const filename = `${product.productCode}${index}`;
        return {
          file,
          filename,
          filetype: file.type,
          vendor: vendor.vendorName,
          page: product.productCode,
        };
      });
  
      setSelectedFile(newSelectedFiles);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (product.subCategory.length === 0) {
      setSubCategoryError(true);
      showToast("Please select at least one sub-category", "error");
      return;
    }

    try {
      setLoading(true);

      const updatedSelectedFiles = selectedFile.map((item) => {
        const extension = item.filetype.split("/")[1];
        const url = `https://aurum-app.s3.ap-south-1.amazonaws.com/${encodeURIComponent(vendor.vendorName.toLowerCase())}/${encodeURIComponent(product.productCode.toLowerCase())}/${encodeURIComponent(item.filename.toLowerCase())}.${extension}`;
        return {
          ...item,
          vendor: vendor.vendorName,
          imageUrl: url,
        };
      });

      const newImageUrls = updatedSelectedFiles.map((item) => item.imageUrl);
      setProduct((prev) => ({ ...prev, image: newImageUrls }));

      if (newImageUrls.length > 0) {
        for (let i = 0; i < newImageUrls.length; i++) {
          const file = selectedFile[i].file;
          const extension = selectedFile[i].filetype.split("/")[1];
          const filenameWithExt = `${selectedFile[i].filename}.${extension}`;
          const generatedUrl = `/s3/getPresignedUrl?filename=${encodeURIComponent(filenameWithExt)}&filetype=${selectedFile[i].filetype}&page=${product.productCode}&vendor=${vendor.vendorName}`;

          if (!file) {
            continue;
          }

          const presignedUrl = await getPresignedUrl(generatedUrl);
          await uploadToS3(presignedUrl, selectedFile[i]);
        }
      }

      const finalProduct = {
        ...product,
        image: newImageUrls.length > 0 ? newImageUrls : product.image,
        vendor: vendor.vendorName,
        diamondDetails: {
          ...product.diamondDetails,
          diamondEntries: diamondRows,
          solitaireEntries: solitaireRows,
        },
      };

      const res = await apiRequest("/products", {
        method: "POST",
        body: finalProduct,
      });

      if (res) {
        resetFormState();
        showToast("Product saved successfully!", "success");
      }
    } catch (err: any) {
      const errorMessage =
        err?.body?.message ||
        (typeof err?.body === "string" ? err.body : null) ||
        err?.message ||
        "Error creating product";
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Initial product:", product);
  
    let newImageUrls: string[] = [];
  
    if (selectedFile.length > 0) {
      const updatedSelectedFiles = selectedFile.map((item) => {
        const extension = item.filetype.split("/")[1]
        const url = `https://aurum-app.s3.ap-south-1.amazonaws.com/${encodeURIComponent(
          vendor.vendorName.toLowerCase()
        )}/${encodeURIComponent(product.productCode.toLowerCase())}/${encodeURIComponent(item.filename.toLowerCase())}.${extension}`;
        return {
          ...item,
          vendor: vendor.vendorName,
          imageUrl: url,
        };
      });
  
      newImageUrls = updatedSelectedFiles.map((item) => item.imageUrl);
      console.log("New Image URLs:", newImageUrls);
  
      const mergedImageUrls = [...product.image, ...newImageUrls];
      setProduct((prev) => ({ ...prev, image: mergedImageUrls }));
  
      // Upload files
      for (let i = 0; i < updatedSelectedFiles.length; i++) {
        const file = updatedSelectedFiles[i].file;
        if (!file) continue;
  
        const extension = selectedFile[i].filetype.split("/")[1];
        const filenameWithExt = `${selectedFile[i].filename}.${extension}`;
        const generatedUrl = `/s3/getPresignedUrl?filename=${encodeURIComponent(filenameWithExt)}&filetype=${selectedFile[i].filetype}&page=${product.productCode}&vendor=${vendor.vendorName}`;
  
        try {
          const presignedUrl = await getPresignedUrl(generatedUrl);
          await uploadToS3(presignedUrl, updatedSelectedFiles[i]);
        } catch (err) {
          console.error("Error uploading to S3:", err);
        }
      }
    }
  
    // Prepare final product object
    const finalProduct = {
      ...product,
      image: newImageUrls.length > 0 ? [...product.image, ...newImageUrls] : product.image,
      diamondDetails: {
        ...product.diamondDetails,
        diamondEntries: diamondRows,
        solitaireEntries: solitaireRows,
      },
    };

    const resolvedProductId = product?.productId || product?.id;
    console.log("Final product to update:", finalProduct);
    console.log("Resolved product id for update:", resolvedProductId);

    if (!resolvedProductId) {
      showToast("Unable to update: missing product id", "error");
      return;
    }
  
    try {
      const res = await apiRequest(`/products/${resolvedProductId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: finalProduct,
      });
  
      if (!res) throw new Error("Failed to update product");
  
      if (openDialog) openDialog(false);
    } catch (err) {
      console.error("Update error:", err);
    }
  };

  
const handleDragEnd = (result: any) => {
  if (!result.destination) return;

  const reorderedFiles = Array.from(selectedFile);
  const [removed] = reorderedFiles.splice(result.source.index, 1);
  reorderedFiles.splice(result.destination.index, 0, removed);

  setSelectedFile(reorderedFiles);
};
  


      
  const handleSaveQuote = async () => {
          console.log("handle save quote called")

          let quoteImageUrls: string[] = [];

          if (selectedFile.length > 0) {
            const updatedSelectedFiles = selectedFile.map((item) => {
              const extension = item.filetype.split("/")[1];
              const url = `https://aurum-app.s3.ap-south-1.amazonaws.com/${encodeURIComponent(vendor.vendorName.toLowerCase())}/${encodeURIComponent(product.productCode.toLowerCase())}/${encodeURIComponent(item.filename.toLowerCase())}.${extension}`;
              return { ...item, vendor: vendor.vendorName, imageUrl: url };
            });
            quoteImageUrls = updatedSelectedFiles.map((item) => item.imageUrl);

            for (let i = 0; i < updatedSelectedFiles.length; i++) {
              const file = updatedSelectedFiles[i].file;
              if (!file) continue;
              const extension = selectedFile[i].filetype.split("/")[1];
              const filenameWithExt = `${selectedFile[i].filename}.${extension}`;
              const generatedUrl = `/s3/getPresignedUrl?filename=${encodeURIComponent(filenameWithExt)}&filetype=${selectedFile[i].filetype}&page=${product.productCode}&vendor=${vendor.vendorName}`;
              try {
                const presignedUrl = await getPresignedUrl(generatedUrl);
                await uploadToS3(presignedUrl, updatedSelectedFiles[i]);
              } catch (err) {
                console.error("Error uploading to S3:", err);
              }
            }
          }

          const finalProduct = {
            ...product,
            image: quoteImageUrls.length > 0 ? [...product.image, ...quoteImageUrls] : product.image,
            vendor: vendor.vendorName,
            diamondDetails: {
              ...product.diamondDetails,
              diamondEntries: diamondRows,
              solitaireEntries: solitaireRows,
            },
          }

        
          const updatedQuote: QuoteData = {
            ...quoteData,
            product: finalProduct,
          };
  
          console.log(updatedQuote)
        
          try {
            const res = await apiRequest(`/api/quote/update`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: updatedQuote,
            });
        
            if (!res) throw new Error("Failed to update quote");
            if (openDialog) {
              openDialog(false);
            }
        
          } catch (err) {
            console.error("Update error:", err);
          }
  };

const getSubmitHandler = () => {
  console.log("handler called")
  if (isEditMode && isAddMode) 
          {
            console.log("quote handler return")
            return handleSaveQuote;
          }
            
        else if (!isEditMode)
          {
            console.log("save handler return")
            return handleSubmit;
          }
          else{
            console.log("update handler return")
            return handleUpdate;
          }
          
        };

const resetFormState = () => {
  setProduct(defaultProduct);
  setSelectedFile([]);
  setDiamondRows([{ diamondSize: "", diamondCount: 0, totalwt: 0.0 }]);
  setSolitaireRows([{ solitaireSize: 0.0, solitaireCount: 0 }]);
  setSubCategoryError(false);
};

const renderTextField = (
  label: string,
  path: string,
  value: any,
  type = "text",
  isNumeric = false,
  isRequired = false
) => {
  const validationInfo = validation[path] || { isValid: true, message: "" };

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <TextField
          fullWidth
          size="small"
          type={type}
          value={value}
          required={isRequired}
          onChange={(e) =>
            handleChange(
              path,
              isNumeric ? e.target.value.replace(/[^0-9.]/g, "") : e.target.value
            )
          }
          error={!validationInfo.isValid}
          helperText={!validationInfo.isValid ? validationInfo.message : ""}
          InputProps={{
            endAdornment:
              path === "productName" || path === "productCode"
                ? validationInfo.isValid && value ? (
                    <span style={{ color: "green", marginLeft: 8 }}>✅</span>
                  ) : null
                : undefined,
          }}
          inputProps={
            isNumeric
              ? {
                  min: 0,
                  inputMode: "decimal",
                  pattern: "^\\d*\\.?\\d*$",
                }
              : {}
          }
        />
      </TableCell>
    </TableRow>
  );
};

const tableSx = {
  bgcolor: "#ffffff",
  minWidth: { xs: 520, md: 0 },
  "& .MuiTableCell-root": {
    px: { xs: 1, md: 1.25 },
    py: 0.9,
    fontSize: { xs: "0.75rem", md: "0.875rem" },
    verticalAlign: "top",
    borderColor: "#e8e8e8",
  },
  "& .MuiTableCell-root:first-of-type": {
    width: { xs: "40%", md: "34%" },
    fontWeight: 600,
    color: "#333",
  },
  "& .MuiTableRow-root:nth-of-type(odd)": {
    backgroundColor: "#fafafa",
  },
};

const tableWrapSx = {
  overflowX: "auto",
  borderRadius: "12px",
  border: "1px solid #e0e0e0",
  mb: 2,
  boxShadow: "0 1px 6px rgba(0, 0, 0, 0.04)",
  background: "#ffffff",
};


  

// Inside your ProductForm component:
return (
  <Container maxWidth="xl" >
    {loading &&
      <div className="flex justify-center items-center h-[50vh] w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-500"></div>
      </div>
    }

    <form
        onSubmit={(e) => {
          e.preventDefault(); 
          const handler = getSubmitHandler();
          if (handler) {
            handler(e);
          }
        }}
        className="w-full flex flex-col gap-5 p-3 sm:p-5 bg-white rounded-xl shadow-md border border-gray-200"
      >

        <Box
          sx={{
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
            border: "1px solid #e2e8f0",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {isEditMode ? (isAddMode ? "Quote Product" : "Edit Product") : "Create Product"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Quick vendor flow: fill listing basics, set pricing, then optionally add merchandising and summary details.
          </Typography>
          <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            <Chip size="small" label="1. Basic Listing" sx={{ backgroundColor: "#fff7e0" }} />
            <Chip size="small" label="2. Pricing & Metal" sx={{ backgroundColor: "#fff7e0" }} />
            <Chip size="small" label="3. Diamond / Solitaire" sx={{ backgroundColor: "#fff7e0" }} />
            <Chip size="small" label="4. Optional Notes" sx={{ backgroundColor: "#f8f9fa" }} />
            <Chip size="small" label={`Vendor: ${vendor.vendorName || "N/A"}`} variant="outlined" />
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: { xs: 2, md: 4 } }}>

          {/* Left Column */}
          <Box sx={{ flex: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={product.isInventory}
                onChange={(e) => handleChange("isInventory", e.target.checked)}
                color="primary"
              />
            }
            label="Is Inventory"
          />
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>Basic Info</Typography>
              <Box sx={{ flex: 1, height: "2px", backgroundColor: "#d4af37" }} />
            </Box>
            <Box sx={tableWrapSx}>
            <Table size="small" sx={tableSx}>
              <TableBody>
                {renderTextField("Product Name", "productName", product.productName,"text",false,true)}
                {renderTextField("Product Code", "productCode", product.productCode,"text",false,true)}
                {/* {renderTextField("Category", "category", product.category)} */}
                <TableRow>
                <TableCell>Segment</TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    name="segment"
                    select
                    fullWidth
                    value={product.segment ?? SegmentType.DIAMOND_JEWELLERY}
                    onChange={(e) => handleChange("segment", e.target.value as SegmentType)}
                  >
                    {Object.values(SegmentType).map((segment) => (
                      <MenuItem key={segment} value={segment}>
                        {segment.replaceAll("_", " ")}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={product.category || categoryOptions[0] || ""}
                      required
                      onChange={(e) => handleChange("category", e.target.value)}
                    >
                      {categoryOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Sub Category</TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={subCategoryOptions}
                      value={product.subCategory}
                      onChange={handleSubCategoryChange}
                      renderTags={(value: string[], getTagProps) =>
                        value.map((option: string, index: number) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          placeholder="Add one or more sub-categories"
                          error={subCategoryError}
                          helperText={subCategoryError ? "At least one sub-category is required" : ""}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>


                <TableRow>
                  <TableCell>Images</TableCell>
                  <TableCell>
                    <input
                      type="file"
                      required={!isEditMode}
                      multiple
                      onChange={handleFileChange}
                    />
                  </TableCell>
                </TableRow>

  {selectedFile.length > 0 && (
    <TableRow>
      <TableCell colSpan={2}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="files">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="flex flex-col gap-2 mt-2"
              >
                {selectedFile.map((item, index) => (
                  <Draggable key={item.filename} draggableId={item.filename} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="flex items-center gap-4 p-2 border rounded bg-white shadow"
                      >
                        {item.file && (
                          <img
                            src={URL.createObjectURL(item.file)}
                            alt={item.filename}
                            className="w-16 h-16 object-cover rounded"
                          />
                        )}
                        <span className="text-sm">{item.filename}</span>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>


      </TableCell>
    </TableRow>
  )}
                {renderTextField("Price", "price", product.price, "text", true)}
              </TableBody>
            </Table>
            </Box>

            {/* <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Metal Details</Typography> */}
            <Box sx={{ mt: 3, pt: 3, borderTop: "2px solid #e8e8e8" }}>
              <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>Metal Details</Typography>
                <Box sx={{ flex: 1, height: "2px", backgroundColor: "#d4af37" }} />
              </Box>
              <Box sx={tableWrapSx}>
            <Table size="small" sx={tableSx}>
              <TableBody>
                {/* {renderTextField("Metal Name", "metalDetails.metalName", product.metalDetails.metalName)} */}
                <TableRow>
                  <TableCell>Metal Name</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={product.metalDetails.metalName || metalColors[0] || "yellow gold"}
                      required
                      onChange={(e) => handleChange("metalDetails.metalName", e.target.value)}
                    >
                      {metalColors.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
                {/* {renderTextField("Purity", "metalDetails.purity", product.metalDetails.purity)} */}
                <TableRow>
                  <TableCell>Purity</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={product.metalDetails.purity ?? metalPurity[0] ?? 18}
                      required
                      onChange={(e) => handleChange("metalDetails.purity", e.target.value)}
                    >
                      {metalPurity.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}k
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
                {renderTextField("Metal Wt", "metalDetails.metalWt", product.metalDetails.metalWt,"number", true, true)}
              </TableBody>
            </Table>
            </Box>
            </Box>
          </Box>

          {/* Right Column */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>Making Charges</Typography>
              <Box sx={{ flex: 1, height: "2px", backgroundColor: "#d4af37" }} />
            </Box>
            <Box sx={tableWrapSx}>
            <Table size="small" sx={tableSx}>
              <TableBody>
                {/* {renderTextField("Metal Price", "priceBreakup.metal", product.priceBreakup.metal, "text", true)}
                {renderTextField("Diamond Price", "priceBreakup.diamond", product.priceBreakup.diamond, "text", true)} */}
                {renderTextField("Making Charges %", "makingPercentage", product.makingPercentage, "text", true)}
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "slategray", // or "#94a3b8" for slate-400
                    color: "white",
                  }}
                  colSpan={2}
                >
                  Note: If provided will be used to calculate the making charges. If not default of 5% will be applied.
                </TableCell>
              </TableRow>

                {/* {renderTextField("GST", "priceBreakup.gst", product.priceBreakup.gst, "text", true)}
                {renderTextField("GST %", "priceBreakup.gstPercent", product.priceBreakup.gstPercent, "text", true)}
                {renderTextField("Total Price", "priceBreakup.total", product.priceBreakup.total, "text", true)} */}
              </TableBody>
            </Table>
            </Box>

            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>Merchandising</Typography>
              <Box sx={{ flex: 1, height: "2px", backgroundColor: "#d4af37" }} />
            </Box>
            <Box sx={tableWrapSx}>
              <Table size="small" sx={tableSx}>
                <TableBody>
                  <TableRow>
                    <TableCell>Occasion</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        select
                        fullWidth
                        value={product.occasion || occasionOptions[0] || ""}
                        onChange={(e) => handleChange("occasion", e.target.value)}
                      >
                        {occasionOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Certification</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        select
                        fullWidth
                        value={product.certification || diamondCertificationOptions[0] || "IGI"}
                        onChange={(e) => handleChange("certification", e.target.value)}
                      >
                        {diamondCertificationOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Gender</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        select
                        fullWidth
                        value={product.gender || "unisex"}
                        onChange={(e) => handleChange("gender", e.target.value)}
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="unisex">Unisex</MenuItem>
                      </TextField>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Tags</TableCell>
                    <TableCell>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={tagsOptions}
                        value={product.tags}
                        onChange={(event, newValue) => handleChange("tags", newValue)}
                        renderTags={(value: string[], getTagProps) =>
                          value.map((option: string, index: number) => (
                            <Chip size="small" variant="outlined" label={option} {...getTagProps({ index })} />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" size="small" placeholder="ex: wedding, festive" />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>

        { product.segment === SegmentType.DIAMOND_JEWELLERY && (
          <div>
            <Box sx={{ mt: 3, pt: 3, borderTop: "2px solid #e8e8e8" }}>
              <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>Diamond Details</Typography>
                <Box sx={{ flex: 1, height: "2px", backgroundColor: "#d4af37" }} />
              </Box>
              <Box sx={tableWrapSx}>
                <Table size="small" sx={tableSx}>
                  <TableBody>
                    <TableRow>
                    <TableCell>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Clarity"
                          value={product.diamondDetails.diamonds.clarity || clarities[0] || ""}
                          onChange={(e) => handleChange("diamondDetails.diamonds.clarity", e.target.value)}
                        >
                          {clarities.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Color"
                          value={product.diamondDetails.diamonds.color || colors[0] || ""}
                          onChange={(e) => handleChange("diamondDetails.diamonds.color", e.target.value)}
                        >
                          {colors.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    </TableRow>
                      

                      <TableRow>
                      <TableCell>Setting Type</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          value={product.diamondDetails.diamonds.settingType || settingTypes[0] || ""}
                          onChange={(e) => handleChange("diamondDetails.diamonds.settingType", e.target.value)}
                        >
                          {settingTypes.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      </TableRow>

                      <TableRow>
                      <TableCell>Shape</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          value={product.diamondDetails.diamonds.shape || diamonShapes[0] || ""}
                          onChange={(e) => handleChange("diamondDetails.diamonds.shape", e.target.value)}
                        >
                          {diamonShapes.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>Additional Coverage charges</TableCell>
                         <TableCell>
                          <TextField
                            size="small"
                            value={product.diamondDetails.diamonds.additionalCharges}
                            onChange={(e) => handleChange("diamondDetails.diamonds.additionalCharges", e.target.value)}
                            placeholder="Count"
                            type="number"
                          />
                        </TableCell>
                      </TableRow>
                  
                  </TableBody>
                </Table>
                </Box>
              </Box>

              <Box sx={{ mt: 3, pt: 0 }}>
                <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>Diamond Entries</Typography>
                  <Box sx={{ flex: 1, height: "2px", backgroundColor: "#d4af37" }} />
                </Box>
                  
                  {diamondRows.length === 0 ? (
                    <Box sx={{ py: 3, textAlign: "center", backgroundColor: "#f9f7f4", borderRadius: "8px", border: "1px dashed #d4af37" }}>
                      <Typography variant="body2" color="textSecondary">No diamond entries yet. Click the button below to add one.</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2.5} sx={{ mb: 3 }}>
                      {diamondRows.map((row, index) => (
                        <Grid item xs={12} sm={6} lg={4} key={index}>
                          <Box
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: "12px",
                              padding: 2.5,
                              backgroundColor: "#ffffff",
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              boxShadow: "0 2px 8px rgba(212, 175, 55, 0.08)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 16px rgba(212, 175, 55, 0.15)",
                                borderColor: "#d4af37",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>Diamond #{index + 1}</Typography>
                              {diamondRows.length > 1 && (
                                <IconButton
                                  onClick={() => removeDiamondRow(index)}
                                  color="error"
                                  size="small"
                                  sx={{ "&:hover": { backgroundColor: "#ffebee" } }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                            <TextField
                              size="small"
                              select
                              fullWidth
                              value={row.diamondSize || sieveSizes[0] || ""}
                              label="Diamond Size"
                              onChange={(e) => handleDiamondChange(index, "diamondSize", e.target.value)}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                            >
                              {sieveSizes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                            <TextField
                              size="small"
                              fullWidth
                              value={row.diamondCount}
                              onChange={(e) => handleDiamondChange(index, "diamondCount", e.target.value)}
                              label="Diamond Count"
                              type="number"
                              inputProps={{ min: 0 }}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                            />
                            <TextField
                              size="small"
                              fullWidth
                              value={row.totalwt}
                              onChange={(e) => handleDiamondChange(index, "totalwt", e.target.value)}
                              label="Total Weight"
                              type="number"
                              inputProps={{ min: 0 }}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => addDiamondRow()}
                      sx={{
                        backgroundColor: "#d4af37",
                        color: "#000",
                        fontWeight: 600,
                        "&:hover": { backgroundColor: "#c29a2e" },
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 3,
                      }}
                    >
                      Add Diamond Entry
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ mt: 3, pt: 0 }}>
                  <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>Solitaire Details</Typography>
                    <Box sx={{ flex: 1, height: "2px", backgroundColor: "#d4af37" }} />
                  </Box>

                  <Box sx={{ mb: 3, p: 2.5, backgroundColor: "#faf8f5", borderRadius: "12px", border: "1px solid #ede9e0" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "#666" }}>Solitaire Properties</Typography>
                        <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Clarity"
                          value={product.diamondDetails.solitaire.stClarity || clarities[0] || ""}
                          onChange={(e) => handleChange("diamondDetails.solitaire.stClarity", e.target.value)}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                        >
                          {clarities.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Color"
                          value={product.diamondDetails.solitaire.stColor || colors[0] || ""}
                          onChange={(e) => handleChange("diamondDetails.solitaire.stColor", e.target.value)}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                        >
                          {colors.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Setting Type"
                          value={product.diamondDetails.solitaire.stSettingType || settingTypes[0] || ""}
                          onChange={(e) => handleChange("diamondDetails.solitaire.stSettingType", e.target.value)}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                        >
                          {settingTypes.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Shape"
                          value={product.diamondDetails.solitaire.stShape || diamonShapes[0] || ""}
                          onChange={(e) => handleChange("diamondDetails.solitaire.stShape", e.target.value)}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                        >
                          {diamonShapes.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          options={solitaireSizes}
                          value={product.diamondDetails.solitaire.availableSizes}
                          onChange={(event, newValue) => handleChange("diamondDetails.solitaire.availableSizes", newValue)}
                          getOptionLabel={(option) => option.toString()} 
                          renderTags={(value: number[], getTagProps) =>
                            value.map((option: number, index: number) => (
                              <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField {...params} variant="outlined" size="small" label="Available Sizes" />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2.5, color: "#666" }}>Solitaire Entries</Typography>
                    {solitaireRows.length === 0 ? (
                      <Box sx={{ py: 3, textAlign: "center", backgroundColor: "#f9f7f4", borderRadius: "8px", border: "1px dashed #d4af37" }}>
                        <Typography variant="body2" color="textSecondary">No solitaire entries yet. Click the button below to add one.</Typography>
                      </Box>
                    ) : (
                      <Grid container spacing={2.5} sx={{ mb: 2 }}>
                        {solitaireRows.map((row, index) => (
                          <Grid item xs={12} sm={6} lg={4} key={index}>
                            <Box
                              sx={{
                                border: "1px solid #e0e0e0",
                                borderRadius: "12px",
                                padding: 2.5,
                                backgroundColor: "#ffffff",
                                position: "relative",
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                boxShadow: "0 2px 8px rgba(212, 175, 55, 0.08)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  boxShadow: "0 4px 16px rgba(212, 175, 55, 0.15)",
                                  borderColor: "#d4af37",
                                },
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>Solitaire #{index + 1}</Typography>
                                {solitaireRows.length > 1 && (
                                  <IconButton
                                    onClick={() => removeSolitaireRow(index)}
                                    color="error"
                                    size="small"
                                    sx={{ "&:hover": { backgroundColor: "#ffebee" } }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              <TextField
                                size="small"
                                select
                                fullWidth
                                value={row.solitaireSize || solitaireSizes[0] || 0}
                                label="Solitaire Size"
                                onChange={(e) => handleSolitaireChange(index, "solitaireSize", e.target.value)}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                              >
                                {solitaireSizes.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}ct
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                size="small"
                                fullWidth
                                value={row.solitaireCount}
                                onChange={(e) => handleSolitaireChange(index, "solitaireCount", e.target.value)}
                                label="Solitaire Count"
                                type="number"
                                inputProps={{ min: 0 }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => addSolitaireRow()}
                      sx={{
                        backgroundColor: "#d4af37",
                        color: "#000",
                        fontWeight: 600,
                        "&:hover": { backgroundColor: "#c29a2e" },
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 3,
                      }}
                    >
                      Add Solitaire Entry
                    </Button>
                  </Box>
                </Box>
              </div>

              )}

        <Box component="details" sx={{ mt: 2, border: "1px solid #e6e6e6", borderRadius: 2, backgroundColor: "#fcfcfc" }}>
          <Box
            component="summary"
            sx={{
              cursor: "pointer",
              listStyle: "none",
              p: 1.5,
              "&::-webkit-details-marker": { display: "none" },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
              Optional: Product Summary & Notes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fill this when you want richer product descriptions for catalog display.
            </Typography>
          </Box>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Box sx={tableWrapSx}>
              <Table size="small" sx={tableSx}>
                <TableBody>
                  {renderTextField("Width", "productSummary.width", product.productSummary.width)}
                  {renderTextField("Gross Wt", "productSummary.grossWt", product.productSummary.grossWt)}
                  {renderTextField("Description", "productDescription", product.productDescription)}
                  {renderTextField("Note", "note", product.note)}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            position: { xs: "sticky", md: "sticky" },
            bottom: 8,
            zIndex: 5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(6px)",
          }}
        >
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            disabled={loading}
            onClick={() => {
              if (isEditMode && openDialog) {
                openDialog(false);
                return;
              }
              resetFormState();
            }}
          >
            {isEditMode ? "Cancel" : "Reset"}
          </Button>

          <Button type="submit" variant="contained" disabled={loading}>
            {loading
              ? "Saving..."
              : isEditMode
              ? isAddMode
                ? "Update Quote"
                : "Update Product"
              : "Save Product"}
          </Button>
        </Box>
    </form>
    <Snackbar
      open={toastOpen}
      autoHideDuration={3000}
      onClose={() => setToastOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity={toastSeverity} variant="filled" onClose={() => setToastOpen(false)}>
        {toastMessage}
      </Alert>
    </Snackbar>

  </Container>
);


};

export default AddProduct;

